/**
 * iOS Focus Guard - Prevents keyboard constraint conflicts in WKWebView
 *
 * Problem: iOS WebView and JavaScript simultaneously control focus during
 * keyboard transitions, causing RTIInputSystemClient constraint conflicts.
 *
 * Solution: Delay focus() calls during keyboard transitions on iOS WebView.
 *
 * Usage:
 *   import { safeFocus, isIOSWebView } from "../lib/ios_focus_guard"
 *
 *   // Instead of: element.focus()
 *   safeFocus(element)
 *
 *   // With options: element.focus({ preventScroll: true })
 *   safeFocus(element, { preventScroll: true })
 */

// Configuration constants
const IOS_FOCUS_GUARD_CONFIG = {
  transitionDelayMs: 250,       // Delay during keyboard transitions
  stableDelayMs: 50,            // Normal delay when keyboard is stable
  transitionWindowMs: 400       // Time window to consider keyboard "in transition"
}

// Track keyboard transition state
let keyboardTransitionTimestamp = 0
let pendingFocusTimeout = null

/**
 * Check if running in iOS WebView (Turbo Native iOS)
 * @returns {boolean}
 */
export function isIOSWebView() {
  const ua = navigator.userAgent || ""
  return document.documentElement.classList.contains("turbo-native") &&
         ua.includes("Turbo Native iOS")
}

/**
 * Check if keyboard is currently in transition
 * @returns {boolean}
 */
function isKeyboardTransitioning() {
  return Date.now() - keyboardTransitionTimestamp < IOS_FOCUS_GUARD_CONFIG.transitionWindowMs
}

/**
 * Mark keyboard as transitioning (called by keyboard event handlers)
 */
export function markKeyboardTransition() {
  keyboardTransitionTimestamp = Date.now()
}

/**
 * Safe focus with iOS guard
 *
 * On iOS WebView, delays focus() calls to prevent keyboard constraint conflicts.
 * On other platforms, calls focus() with minimal delay for consistency.
 *
 * @param {HTMLElement} element - Element to focus
 * @param {FocusOptions} [options] - Focus options (passed to element.focus())
 * @param {Object} [guardOptions] - Guard-specific options
 * @param {boolean} [guardOptions.skipGuard] - Skip iOS guard entirely
 * @param {boolean} [guardOptions.immediate] - Focus immediately without delay
 */
export function safeFocus(element, options = {}, guardOptions = {}) {
  if (!element || typeof element.focus !== "function") {
    return
  }

  // Skip guard if explicitly requested
  if (guardOptions.skipGuard) {
    element.focus(options)
    return
  }

  // Immediate focus requested
  if (guardOptions.immediate) {
    element.focus(options)
    return
  }

  // Non-iOS: use minimal delay for consistency
  if (!isIOSWebView()) {
    element.focus(options)
    return
  }

  // iOS WebView: apply guarded focus with delay

  // Clear any pending focus
  if (pendingFocusTimeout) {
    clearTimeout(pendingFocusTimeout)
  }

  // Calculate appropriate delay based on keyboard state
  const delay = isKeyboardTransitioning()
    ? IOS_FOCUS_GUARD_CONFIG.transitionDelayMs
    : IOS_FOCUS_GUARD_CONFIG.stableDelayMs

  // Schedule focus with delay
  pendingFocusTimeout = setTimeout(() => {
    // Double-check element is still in DOM and focusable
    if (document.body.contains(element)) {
      element.focus(options)
    }
    pendingFocusTimeout = null
  }, delay)
}

/**
 * Cancel any pending focus operation
 */
export function cancelPendingFocus() {
  if (pendingFocusTimeout) {
    clearTimeout(pendingFocusTimeout)
    pendingFocusTimeout = null
  }
}

// ============================================================
// iOS Auto-Zoom Reset
// ============================================================

// Prevents concurrent zoom reset operations
let isResettingZoom = false

/**
 * Reset iOS auto-zoom on input blur
 *
 * iOS WebView auto-zooms when focusing inputs with font-size < 16px.
 * This zoom persists after blur, requiring manual pinch-out.
 *
 * Solution: Briefly set viewport maximum-scale=1, then restore original.
 * This forces WebView to reset zoom level.
 *
 * Safety measures:
 * - isResettingZoom flag prevents concurrent operations
 * - Regex replacement (not append) prevents content accumulation
 * - Skips if already at maximum-scale=1
 * - Double-RAF ensures zoom reset completes before restoring
 */
export function resetIOSZoom() {
  if (!isIOSWebView()) return
  if (isResettingZoom) return

  const viewport = document.querySelector('meta[name="viewport"]')
  if (!viewport) return

  const originalContent = viewport.getAttribute('content')

  // Skip if already at maximum-scale=1 (not zoomed)
  if (/maximum-scale=1(?:[,\s]|$)/.test(originalContent)) return

  isResettingZoom = true

  // Step 1: Apply maximum-scale=1 (replace existing or append)
  let restrictedContent
  if (/maximum-scale=/.test(originalContent)) {
    restrictedContent = originalContent.replace(/maximum-scale=[\d.]+/, 'maximum-scale=1')
  } else {
    restrictedContent = originalContent + ',maximum-scale=1'
  }

  viewport.setAttribute('content', restrictedContent)

  // Step 2: Restore original after two frames (ensures zoom reset)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      viewport.setAttribute('content', originalContent)
      isResettingZoom = false
    })
  })
}

/**
 * Setup automatic zoom reset on input/textarea blur (iOS only)
 */
function setupIOSZoomReset() {
  if (!isIOSWebView()) return

  document.addEventListener('focusout', (e) => {
    const target = e.target
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      // Delay to allow keyboard animation to complete
      setTimeout(resetIOSZoom, 150)
    }
  }, { passive: true })
}

// Listen for keyboard events to track transitions
if (typeof window !== "undefined") {
  // iOS VisualViewport resize indicates keyboard state change
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", () => {
      markKeyboardTransition()
    })
  }

  // iOS native keyboard events (dispatched by WKWebView)
  window.addEventListener("ios:keyboardChange", () => {
    markKeyboardTransition()
  })

  // Android native keyboard events (dispatched by native app)
  window.addEventListener("android:keyboardChange", () => {
    markKeyboardTransition()
  })

  // Setup iOS zoom reset on blur
  setupIOSZoomReset()
}

export default {
  isIOSWebView,
  safeFocus,
  cancelPendingFocus,
  markKeyboardTransition,
  resetIOSZoom
}
